import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import html2pdf from 'html2pdf.js';

const DocumentViewPage = () => {
  const { documentId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    const fetchDocument = async () => {
      try {
        setLoading(true);
        
        const { data, error } = await supabase
          .from('generated_documents')
          .select('*')
          .eq('id', documentId)
          .single();
          
        if (error) throw error;
        
        // Verify that the user owns this document
        if (data.user_id !== user.id) {
          throw new Error('You do not have permission to view this document.');
        }
        
        setDocumentData(data);
        
        // For viewing the PDF, we'll use the same edge function that works for downloads
        // This ensures we get a properly signed URL for viewing
        if (data.pdf_url) {
          try {
            const { data: signedUrlData, error: signedUrlError } = await supabase.functions.invoke("get-signed-url", {
              method: "POST",
              body: { documentId: documentId, forDownload: false },
            });
            
            if (signedUrlError) {
              console.error("Error invoking edge function for PDF view:", signedUrlError);
            } else if (signedUrlData?.signedUrl) {
              console.log("Successfully retrieved signed URL for PDF view");
              setPdfUrl(signedUrlData.signedUrl);
            }
          } catch (viewError) {
            console.error("Error getting PDF view URL:", viewError);
          }
        }
        
        // If we have an HTML URL but not content, fetch the content
        if (!pdfUrl && data.html_url && !data.html_content && contentRef.current) {
          try {
            // Create signed URL with 1 hour expiry
            const { data: signedData, error: signError } = await supabase
              .storage
              .from('documents')
              .createSignedUrl(data.html_url, 3600);
              
            if (signError) {
              console.error('Error creating signed URL for HTML:', signError);
            } else if (signedData?.signedUrl) {
              const response = await fetch(signedData.signedUrl);
              const html = await response.text();
              contentRef.current.innerHTML = html;
            }
          } catch (htmlError) {
            console.error("Error fetching HTML content:", htmlError);
          }
        }
      } catch (error) {
        console.error('Error fetching document:', error);
        setError(error.message || 'Failed to load this document.');
      } finally {
        setLoading(false);
      }
    };

    if (user && documentId) {
      fetchDocument();
    }
  }, [documentId, user]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleDownloadDocument = async (docId, docTitle) => {
    try {
      setDownloadLoading(true);
      setError(null);

      // Call the edge function to get the signed URL
      const { data, error } = await supabase.functions.invoke("get-signed-url", {
        method: "POST",
        body: { documentId: docId, forDownload: true },
      });

      if (error) {
        console.error("Error invoking edge function:", error);
        setError("Failed to fetch document link. Please try again.");
        return;
      }

      if (!data?.signedUrl) {
        setError("Could not generate document link.");
        return;
      }

      // Trigger PDF download instead of opening in a new tab
      console.log("Downloading document from URL:", data.signedUrl);
      const a = window.document.createElement('a');
      a.href = data.signedUrl;
      a.download = `${docTitle || 'document'}.pdf`;
      window.document.body.appendChild(a);
      a.click();
      window.document.body.removeChild(a);
      
    } catch (error) {
      console.error("Error accessing document:", error);
      setError("Failed to download document. Please try again later.");
    } finally {
      setDownloadLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-10 flex justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="bg-red-50 text-red-600 p-4 rounded-lg mb-6">
          {error}
        </div>
        <button 
          onClick={handleBack}
          className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  if (!documentData) {
    return (
      <div className="container mx-auto px-4 py-10">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Document not found</h2>
          <p className="mb-6">The requested document could not be found.</p>
          <button 
            onClick={handleBack}
            className="px-6 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-10">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold mb-2">{documentData.title}</h1>
          <p className="text-gray-600">
            Created: {new Date(documentData.created_at).toLocaleDateString()}
          </p>
        </div>
        <div className="flex space-x-4">
          <button 
            onClick={handleBack}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 transition-colors flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Back to Dashboard
          </button>
          <button 
            onClick={() => handleDownloadDocument(documentId, documentData.title)}
            disabled={downloadLoading}
            className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors flex items-center disabled:opacity-70"
          >
            {downloadLoading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Preparing Download...
              </>
            ) : (
              <>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
                Download as PDF
              </>
            )}
          </button>
        </div>
      </div>
      
      <div className="max-w-4xl mx-auto bg-white rounded-lg overflow-hidden shadow-lg p-8 border">
        {pdfUrl ? (
          // Display PDF using iframe for better compatibility
          <iframe 
            src={pdfUrl} 
            className="w-full h-screen"
            title="Document Preview"
          />
        ) : (
          // Display HTML content
          <div 
            ref={contentRef}
            className="document-content"
            dangerouslySetInnerHTML={documentData.html_content ? { __html: documentData.html_content } : undefined}
          />
        )}
      </div>
    </div>
  );
};

export default DocumentViewPage;